// src/pages/public/ValidarReceta.js
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { verificarRecetaPublica, registrarDispensacion } from "../../services/recetas";
import "./ValidarReceta.css";


// Helper para formatear como: "Nov. 1, 2025, 5:08 p.m."
function formatFechaFirma(isoString) {
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return isoString || "—";

  const MONTHS = [
    "Jan.", "Feb.", "Mar.", "Apr.", "May.", "Jun.",
    "Jul.", "Aug.", "Sep.", "Oct.", "Nov.", "Dec."
  ];
  const mon = MONTHS[d.getMonth()];
  const day = d.getDate();
  const year = d.getFullYear();

  const minutes = String(d.getMinutes()).padStart(2, "0");
  const hours = d.getHours();
  const h12 = hours % 12 || 12;
  const ampm = hours < 12 ? "a.m." : "p.m.";

  return `${mon} ${day}, ${year}, ${h12}:${minutes} ${ampm}`;
}

function ValidarReceta() {
  const { id } = useParams(); // UUID solo para consulta; NO se muestra
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [netError, setNetError] = useState(false);
  //const [total, setTotal] = useState(false);
 // const [comentarios, setComentarios] = useState("");
  

  useEffect(() => {
    let isMounted = true;
    async function fetchData() {
      setLoading(true);
      setNetError(false);
      try {
        const res = await verificarRecetaPublica(id);
        const data = res?.data ?? res ?? null;
        if (isMounted) setPayload(data);
      } catch (e) {
        console.error("Error verificador:", e);
        if (isMounted) setNetError(true);
      } finally {
        if (isMounted) setLoading(false);
      }
    }
    fetchData();
    return () => {
      isMounted = false;
    };
  }, [id]);

  // Normalización al contrato mínimo acordado:
  // status, firmado_en (formateado), firmado_por, folio, pdf_url
  const status =
    payload?.status ??
    (payload?.es_firmada === true
      ? "valida"
      : payload?.es_firmada === false
      ? "invalida"
      : netError
      ? "no_encontrada"
      : undefined);

  const firmado_por = payload?.firmado_por ?? payload?.cert_subject_cn ?? "—";

  const firmadoEnRaw = payload?.firmado_en ?? payload?.firma_fecha ?? null;
  const firmado_en = firmadoEnRaw ? formatFechaFirma(firmadoEnRaw) : "—";

  const folio = payload?.folio ?? "—";
  
    // Autor (snapshot no sensible)
  const autor = payload?.autor_profesional || null;

  const profesion            = autor?.profesion || "—";
  const especialidad         = Array.isArray(autor?.especialidades) ? autor.especialidades.join(", ") : (autor?.especialidades || "—");
  const isInvalida = status === "invalida";
  const isNoEncontrada = status === "no_encontrada" || (!status && !loading);

  let labelProfEsp = "";

    if (profesion && especialidad) {
      labelProfEsp = `${profesion} / ${especialidad}`;
    }
    else if (profesion) {
      labelProfEsp = profesion;
    }
    else if (especialidad) {
      labelProfEsp = especialidad;
    }
    else {
      labelProfEsp = "";
    }
    const labelCedulas = autor?.cedulas?.length > 0 ? autor.cedulas.join(" / ") : "";

  if (loading) {
    return (
      <div className="validar-receta">
        <div className="vr-wrap">
          <h2 className="vr-title">Validación de receta</h2>
          <p className="vr-muted">Validando receta…</p>
          <div className="vr-card">
            <div className="vr-skeleton-line" />
            <div className="vr-skeleton-line" />
            <div className="vr-skeleton-line" />
          </div>
        </div>
      </div>
    );
  }

  if (isNoEncontrada) {
    return (
      <div className="validar-receta">
        <div className="vr-wrap">
          <h2 className="vr-title">Validación de receta</h2>
          <div className="vr-card">
            <p className="vr-badge vr-badge-warn">⚠️ No encontramos esta receta</p>
            <p>No existe una receta asociada a este enlace.</p>
            <p className="vr-muted">Verifica que el código o enlace sea correcto.</p>
          </div>
        </div>
      </div>
    );
  }

  if (isInvalida) {
    return (
      <div className="validar-receta">
        <div className="vr-wrap">
          <h2 className="vr-title">Validación de receta</h2>
          <div className="vr-card">
            <p className="vr-badge vr-badge-error">❌ No pudimos validar la firma</p>
            <p>La firma digital de esta receta no es válida.</p>
            <p className="vr-muted">Verifique con el emisor de la receta.</p>
          </div>
        </div>
      </div>
    );
  }

  // status === "valida"
  return (
  <div className="validar-receta">
    <div className="vr-wrap">
      <h2 className="vr-title">Validación de receta</h2>

      <div className="vr-card">
        <p className="vr-badge vr-badge-ok">✅ Receta firmada digitalmente</p>

        {/* === VIGENCIA === */}
        {payload?.vigencia_receta && (() => {
          const hoy = new Date();
          const fechaVigencia = new Date(payload.vigencia_receta);
          const vigente = hoy <= fechaVigencia;
          const fechaSoloDia = fechaVigencia.toLocaleDateString("es-MX", {
            year: "numeric",
            month: "long",
            day: "numeric"
          });

          return (
            <>
              <p
                className="vr-badge"
                style={{
                  backgroundColor: vigente ? "#28a745" : "#dc3545",
                  color: "white",
                  fontWeight: 600,
                  marginBottom: "0.5em"
                }}
              >
                {vigente ? "🟩 Receta vigente" : "🟥 Receta vencida"}
              </p>
              <div className="vr-row">
                <span className="vr-label">Vigente hasta</span>
                <span className="vr-value">{fechaSoloDia}</span>
              </div>
            </>
          );
        })()}

        <div className="vr-row">
          <span className="vr-label">Firmado por</span>
          <span className="vr-value">{firmado_por}</span>
        </div>

        <div className="vr-row">
          <span className="vr-label">Firmado en</span>
          <span className="vr-value">{firmado_en}</span>
        </div>

        <div className="vr-row">
          <span className="vr-label">Folio</span>
          <span className="vr-value">{folio}</span>
        </div>

        <div className="vr-row">
          <span className="vr-label">Profesión / Especialidad</span>
          <span className="vr-value">{labelProfEsp}</span>
        </div>

        <div className="vr-row">
          <span className="vr-label">Cédula(s)</span>
          <span className="vr-value">{labelCedulas}</span>
        </div>

        {/* === DATOS CLÍNICOS === */}
        {payload?.nombre_paciente && (
          <div className="vr-row">
            <span className="vr-label">Paciente</span>
            <span className="vr-value">{payload.nombre_paciente}</span>
          </div>
        )}

        {payload?.tratamiento && (
          <div className="vr-row">
            <span className="vr-label">Tratamiento</span>
            <span className="vr-value">{payload.tratamiento}</span>
          </div>
        )}

        {Array.isArray(payload?.medicamentos) && payload.medicamentos.length > 0 && (
          <div className="vr-row">
            <span className="vr-label">Medicamentos</span>
            <span className="vr-value">
              <ul style={{ margin: 0, paddingLeft: "1.2em" }}>
                {payload.medicamentos.map((m, i) => (
                  <li key={i}>
                    {m.nombre}
                    {m.indicaciones ? ` — ${m.indicaciones}` : ""}
                  </li>
                ))}
              </ul>
            </span>
          </div>
        )}

        {/* === DISPENSACIÓN (solo si está vigente) === */}
        {/*{(() => {
          const hoy = new Date();
          const fechaVigencia = new Date(payload?.vigencia_receta);
          const vigente = hoy <= fechaVigencia;

          if (!vigente) {
            return (
              <p style={{ color: "red", fontWeight: 600, marginTop: "1em" }}>
                Esta receta ha vencido y no puede dispensarse.
              </p>
            );
          }

          if (!payload?.tiene_dispensacion_total) {
            return (
              <div className="dispensacion-card">
                <h3>Registrar dispensación</h3>

                <div className="dispensacion-field-check">
                  <label>
                    <input
                      type="checkbox"
                      checked={total}
                      onChange={(e) => setTotal(e.target.checked)}
                    />{" "}
                    Dispensación TOTAL
                  </label>
                </div>

                {!total && (
                  <textarea
                    className="dispensacion-textarea"
                    placeholder="Comentarios (opcional en parcial)"
                    value={comentarios}
                    onChange={(e) => setComentarios(e.target.value)}
                  />
                )}

                <button
                  className="dispensacion-btn"
                  onClick={async () => {
                    try {
                      await registrarDispensacion(id, {
                        tipo: total ? "TOTAL" : "PARCIAL",
                        detalles: comentarios,
                      });
                      alert("Dispensación registrada");
                      window.location.reload();
                    } catch (err) {
                      const msg =
                        err?.response?.data?.error ||
                        err?.response?.data?.detail ||
                        "Error al registrar dispensación";
                      alert(msg);
                    }
                  }}
                >
                  Guardar dispensación
                </button>
              </div>
            );
          }

          return (
            <p style={{ color: "green", fontWeight: 600 }}>
              Esta receta YA fue DISPENSADA TOTALMENTE.
            </p>
          );
        })()}*/}
      </div>
    </div>
  </div>
);

}

export default ValidarReceta;
